<template>
  <div>
    <div class="flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
      <div>
        <h2>{{ t('legalEntityAffiliation') }}</h2>
        <p class="text-xs text-obsidian-500">{{ t('legalEntityAffiliationHint') }}</p>
      </div>
      <CVButton
        class="w-full md:w-auto"
        @click.prevent="push({ legalEntityId: null, fromDate: null })"
      >
        {{ t('addLegalEntity') }}
      </CVButton>
    </div>

    <div v-if="fields.length" class="mb-4 mt-4 rounded-md bg-white pb-1 pl-4 pr-4 pt-1">
      <div
        v-for="(field, i) in fields"
        :key="field.key"
        class="relative pb-3 pr-8 pt-3"
        :class="{ 'border-b': i < fields.length - 1 }"
      >
        <div class="grid grid-cols-2 gap-4">
          <SelectField
            :name="`legalEntities[${i}].legalEntityId`"
            :label="t('legalEntity')"
            :options="legalEntityOptions"
            empty-value-to="null"
          />
          <DatepickerField
            v-if="i > 0"
            :name="`legalEntities[${i}].fromDate`"
            :label="t('fromDate')"
          />
          <LabeledText v-else :label="t('fromDate')">-</LabeledText>
        </div>
        <CVButton
          v-if="fields.length > 1"
          variant="ghost"
          size="iconXs"
          class="absolute bottom-5 right-0 text-bittersweet-600"
          :aria-label="t('remove')"
          @click.prevent="remove(i)"
        >
          <XIcon class="size-4" />
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFieldArray } from 'vee-validate';
import { XIcon } from 'lucide-vue-next';
import CVButton from '@/components/buttons/CVButton.vue';
import DatepickerField from '@/components/DatepickerField.vue';
import LabeledText from '@/components/LabeledText.vue';
import SelectField from '@/components/SelectField.vue';
import { useLegalEntities } from '@/queries/use-legal-entities';

interface CarLegalEntityFormValues {
  legalEntityId: string | null;
  fromDate: string | null;
}

const { t } = useI18n();
const { push, remove, fields } = useFieldArray<CarLegalEntityFormValues>('legalEntities');
const { data: legalEntities } = useLegalEntities();

const legalEntityOptions = computed(() => [
  { label: t('selectLegalEntity'), value: null },
  ...(legalEntities.value ?? []).map((legalEntity) => ({
    label: legalEntity.name,
    value: legalEntity.id,
  })),
]);
</script>

<i18n lang="json">
{
  "en": {
    "legalEntityAffiliation": "Legal entity",
    "legalEntityAffiliationHint": "Owning legal entity of the car. Only used to recognise internal rentals; bookings always belong to the legal entity of the station.",
    "addLegalEntity": "+ Add legal entity",
    "legalEntity": "Legal entity",
    "selectLegalEntity": "Select legal entity",
    "fromDate": "From",
    "remove": "Remove"
  },
  "de": {
    "legalEntityAffiliation": "Gesellschaft",
    "legalEntityAffiliationHint": "Gesellschaft, der das Fahrzeug zugeordnet ist. Dient nur zum Erkennen interner Vermietungen; Buchungen gehören immer zur Gesellschaft der Station.",
    "addLegalEntity": "+ Gesellschaft hinzufügen",
    "legalEntity": "Gesellschaft",
    "selectLegalEntity": "Gesellschaft wählen",
    "fromDate": "Ab",
    "remove": "Entfernen"
  },
  "es": {
    "legalEntityAffiliation": "Sociedad",
    "legalEntityAffiliationHint": "Sociedad a la que pertenece el vehículo. Solo sirve para reconocer alquileres internos; las reservas siempre pertenecen a la sociedad de la estación.",
    "addLegalEntity": "+ Añadir sociedad",
    "legalEntity": "Sociedad",
    "selectLegalEntity": "Seleccionar sociedad",
    "fromDate": "Desde",
    "remove": "Eliminar"
  }
}
</i18n>
